import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import SydButton from '../../../../../../commonDesign/Button'
import ChatPanel from './ChatPanel'
import { useAdminPlaygroundApp } from './adminPlaygroundApp'

const useStyles = makeStyles(() => ({
  playground: {
    position: 'absolute',
    top: '60px',
    right: 0,
    bottom: 0,
    width: '400px',
    height: 'auto',
    background: 'white',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(100%)',
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
    opacity: 0,
    '&.slide-in': {
      transform: 'translateX(0)',
      opacity: 1
    }
  },
  closeButton: {
    position: 'absolute',
    top: '6px',
    right: '100%',
    marginRight: '6px',
    zIndex: 1001
  }
}))

function AbundanceDesigner ({ onClose, onJsxUpdate, onAttachmentLoadStart, onThreadIdUpdate, initialThreadId, currentJsxContent, viewId }) {
  const classes = useStyles()
  const playgroundApp = useAdminPlaygroundApp(initialThreadId, viewId, currentJsxContent)
  const containerRef = useRef(null)
  const [isSlideIn, setIsSlideIn] = useState(false)
  const [loadingAttachmentId, setLoadingAttachmentId] = useState(null)

  // Update parent with threadId when it changes
  useEffect(() => {
    if (playgroundApp.threadId && onThreadIdUpdate) {
      onThreadIdUpdate(playgroundApp.threadId)
    }
  }, [playgroundApp.threadId, onThreadIdUpdate])

  // Slide in animation on mount
  useLayoutEffect(() => {
    // Trigger slide-in animation after a brief delay to ensure element is rendered
    const timer = setTimeout(() => {
      setIsSlideIn(true)
    }, 50)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const onWheel = e => {
      // if horizontal swipe/scroll (e.g. two-finger swipe on mac)
      if (e.deltaX !== 0) {
        e.preventDefault()
      }
    }

    // attach with passive: false so we can preventDefault
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  const handleJsxAttachmentClick = async (attachment) => {
    try {
      // Set loading state for this specific attachment
      setLoadingAttachmentId(attachment.threadMessageAttachmentId)

      // Notify parent that attachment loading started
      if (onAttachmentLoadStart) {
        onAttachmentLoadStart()
      }

      // Load the attachment data and update the main content window
      const attachmentData = await playgroundApp.loadAttachmentData(attachment.threadId, attachment.threadMessageAttachmentId)
      if (attachmentData && onJsxUpdate) {
        onJsxUpdate(attachmentData.data, true) // true indicates this is from attachment
      }
    } catch (error) {
      console.error('Error loading attachment:', error)
    } finally {
      // Clear loading state
      setLoadingAttachmentId(null)
    }
  }

  const handleClose = () => {
    setIsSlideIn(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  return (
    <div
      ref={containerRef}
      className={`${classes.playground} ${isSlideIn ? 'slide-in' : ''}`}
    >
      <div className={classes.closeButton}>
        <SydButton
          icon='close'
          size='sm'
          variant='ai'
          onClick={handleClose}
        >
          Close
        </SydButton>
      </div>

      <ChatPanel
        messages={playgroundApp.thread?.messages || []}
        onNewMessage={playgroundApp.addMessage}
        isLoading={playgroundApp.isThreadLoading}
        isErrored={playgroundApp.isErrored}
        onJsxAttachmentClick={handleJsxAttachmentClick}
        loadingAttachmentId={loadingAttachmentId}
      />
    </div>
  )
}

AbundanceDesigner.propTypes = {
  onClose: PropTypes.func.isRequired,
  onJsxUpdate: PropTypes.func,
  onAttachmentLoadStart: PropTypes.func,
  onThreadIdUpdate: PropTypes.func,
  initialThreadId: PropTypes.string,
  currentJsxContent: PropTypes.string,
  viewId: PropTypes.string
}

export default AbundanceDesigner
